// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ThemeType} from '@app/vendor/constants/UserConstants';
import {ThemeTypes} from '@app/vendor/constants/UserConstants';
import {useEffect, useRef, useState} from 'react';
import {THEME_COLOR_VARIABLES, THEME_FONT_VARIABLES} from './themeConstants';

export interface ThemeHydrationResult {
	systemPrefersDark: boolean;
	defaultVariableValues: Record<string, string> | null;
}

export function useThemeHydration(themePreference: ThemeType, customThemeCss: string): ThemeHydrationResult {
	const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
		return window.matchMedia?.('(prefers-color-scheme: dark)').matches;
	});
	const [defaultVariableValues, setDefaultVariableValues] = useState<Record<string, string> | null>(null);

	useEffect(() => {
		if (!window.matchMedia) return;
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = (event: MediaQueryListEvent) => {
			setSystemPrefersDark(event.matches);
		};
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	const hydrationKey =
		themePreference === ThemeTypes.SYSTEM
			? `${themePreference}-${systemPrefersDark ? 'dark' : 'light'}`
			: themePreference;

	const lastHydrationKey = useRef(hydrationKey);

	useEffect(() => {
		if (hydrationKey !== lastHydrationKey.current) {
			setDefaultVariableValues(null);
			lastHydrationKey.current = hydrationKey;
		}

		const frameId = requestAnimationFrame(() => {
			const computed = getComputedStyle(document.documentElement);
			const nextDefaults: Record<string, string> = {};

			for (const variableName of [...THEME_COLOR_VARIABLES, ...THEME_FONT_VARIABLES]) {
				const raw = computed.getPropertyValue(variableName);
				if (raw && raw.trim().length > 0) {
					nextDefaults[variableName] = raw.trim();
				}
			}

			setDefaultVariableValues(nextDefaults);
		});

		return () => cancelAnimationFrame(frameId);
	}, [hydrationKey, customThemeCss]);

	return {systemPrefersDark, defaultVariableValues};
}
